import { InfoTip } from "@/components/ui/info-tip";
import { createFileRoute } from "@tanstack/react-router";
import { Users } from "lucide-react";
import { Permission, useCan } from "@/components/session-provider";
import { PeoplePanel } from "@/components/admin/PeoplePanel";

export const Route = createFileRoute("/people/")({
  component: PeoplePage,
});

/**
 * The staff roster, as its own destination.
 *
 * ⚠️ It used to be a tab inside Admin, and moving it out is a statement about what it is.
 * Admin is where the shape of the system is configured — roles, teams, categories,
 * permissions — and those are things somebody visits rarely and deliberately. Looking up a
 * colleague is neither: it is something an agent does mid-ticket, several times a day, and
 * burying it two clicks inside a settings area made it feel like administration rather
 * than a tool.
 *
 * The privilege boundary has not moved. Reaching this page still needs `user.manage`, and
 * every action within it is still decided per row by the server — you may not administer
 * anyone whose access is not contained within your own.
 *
 * Role grants live in the person's own details panel — which opens inline on this list, so
 * "find the person" and "change what they can reach" are still one journey rather than
 * two. What moved is where the grant is offered, not who may make it: it is the same
 * audited command to the same endpoint, and the server bounds it the same way.
 */
function PeoplePage() {
  const canManageUsers = useCan(Permission.UserManage);
  const canManageRoles = useCan(Permission.RoleManage);

  if (!canManageUsers) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16 text-center sm:px-6">
        <p className="text-sm text-muted-foreground">
          You do not have access to the staff directory.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-8">
      <header className="group/card mb-6">
        <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
          <Users className="h-6 w-6 text-muted-foreground" aria-hidden />
          People
          <InfoTip id="people-directory" />
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Everyone with an account, and everyone in the company directory who does not have one yet.
        </p>
      </header>

      <PeoplePanel canManageRoles={canManageRoles} />
    </div>
  );
}
